import { ReactElement, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useGenerateAiSurveyResponsesMutation } from '../../redux/survey/survey.endpoints';
import { PortalDsButton, portalDs } from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import notificationService from 'src/services/notification/notification.service';
import { useDispatch } from 'react-redux';

interface AiSampleSizeRadioButtonProps {
  readonly extSurveyId: string;
  readonly zeroDisabled: boolean;
  readonly sampleSizes: number[];
  readonly panelId: string;
  readonly audienceCriteria?: Record<string, string[]>;
  onSubmit?: () => void;
  onCancel?: () => void;
}

const AiSampleSizeRadioButton = ({
  zeroDisabled,
  extSurveyId,
  sampleSizes,
  panelId,
  audienceCriteria,
  onSubmit,
  onCancel,
}: AiSampleSizeRadioButtonProps): ReactElement => {
  const dispatch = useDispatch();
  const [createSurveyResponses] = useGenerateAiSurveyResponsesMutation();
  const [maxCompletes, setMaxCompletes] = useState('0');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async() => {
    if (!panelId) {
      notificationService.handleError(new Error('Panel ID is required'), dispatch, 'Panel ID is required');
      return;
    }

    setIsLoading(true);
    try {
      await createSurveyResponses({
        ext_survey_id: extSurveyId,
        max_completes: maxCompletes,
        panel_id: panelId,
        audience: {
          panel: panelId,
          criteria: audienceCriteria ?? {},
        },
      } as Parameters<typeof createSurveyResponses>[0]).unwrap();
      onSubmit?.();
    } catch (error) {
      notificationService.handleError(error, dispatch, 'Could not add AI panelists. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const isDisabled = maxCompletes === '0' || isLoading;

  return (
    <div className="space-y-4">
      <div>
        <p className={cn(portalDs.filterLabel, 'mb-2')}>
          One-time AI panelists
        </p>
        <div
          className={cn(portalDs.segmentGroup, 'flex flex-wrap')}
          role="group"
          aria-label="AI panelist sample size"
        >
          {sampleSizes.map((size) => {
            const value = String(size);
            const isActive = maxCompletes === value;
            return (
              <button
                key={value}
                type="button"
                className={cn(portalDs.segment, isActive && portalDs.segmentActive)}
                disabled={zeroDisabled && size === 0}
                onClick={() => setMaxCompletes(value)}
              >
                {size.toLocaleString()}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex justify-end gap-2">
        {onCancel ? (
          <PortalDsButton
            type="button"
            variant="secondary"
            size="md"
            disabled={isLoading}
            onClick={onCancel}
          >
            Cancel
          </PortalDsButton>
        ) : null}
        <PortalDsButton
          type="button"
          variant="accent"
          size="md"
          disabled={isDisabled}
          onClick={() => { void handleSubmit(); }}
        >
          {isLoading ? <Loader2 className="size-3.5 animate-spin" /> : null}
          {isLoading ? 'Adding panelists…' : 'Trigger now'}
        </PortalDsButton>
      </div>
    </div>
  );
};

export default AiSampleSizeRadioButton;
